import React, { useState, useEffect } from 'react'
import Confetti from 'react-confetti' // Assuming you are using a package like this for confetti
import CloseButton from 'react-bootstrap/CloseButton' // Assuming you have a CloseButton component
import './styles.css'

interface MemoryCardGame {
  options: any
  answer: any
  handleNextExample: () => void
}

const MemoryGame: React.FC<MemoryCardGame> = ({ options, answer, handleNextExample }) => {
  const [selectedCards, setSelectedCards] = useState<string[]>([])
  const [flippedCards, setFlippedCards] = useState<boolean[]>(Array(12).fill(false))
  const [isMatch, setIsMatch] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const [cards, setCards] = useState<string[]>([])
  const [progress, setProgress] = useState<number>(0)
  const [confetti, setConfetti] = useState<boolean>(false)
  const [successModalShow, setSuccessModalShow] = useState<boolean>(false)
  const [wrongModalShow, setWrongModalShow] = useState<boolean>(false)

  const uniqueElements = ['🔴', '⭐', '🟢']

  useEffect(() => {
    const initialDeck = [...options, ...uniqueElements, ...options, ...uniqueElements].slice(0, 12)
    const deck = secureShuffle(initialDeck)
    setCards(deck)
  }, [options])

  function secureShuffle(array: any[]) {
    const arr = [...array]
    const randomValues = new Uint32Array(arr.length)
    crypto.getRandomValues(randomValues)

    for (let i = arr.length - 1; i > 0; i--) {
      const j = randomValues[i] % (i + 1)
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }

    return arr
  }

  useEffect(() => {
    if (successModalShow || wrongModalShow) {
      const timer = setTimeout(() => {
        successModalShow ? handleNext() : handleRetry()
      }, 5000)

      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress < 100) return prevProgress + 2
          return prevProgress
        })
      }, 100)

      return () => {
        clearTimeout(timer)
        clearInterval(progressInterval)
      }
    }
  }, [successModalShow, wrongModalShow])

  useEffect(() => {
    if (selectedCards.length === 2) {
      const [firstCard, secondCard] = selectedCards
      if (firstCard === secondCard) {
        if (firstCard === answer) {
          setIsMatch(true)
          setSuccess(true)
          setConfetti(true)
          setSuccessModalShow(true)
        } else {
          setIsMatch(false)
          setWrongModalShow(true)
        }
      } else {
        setTimeout(() => {
          resetGame()
        }, 1000)
      }
    }
  }, [selectedCards])

  const handleCardClick = (index: number, card: string) => {
    if (flippedCards[index] || selectedCards.length === 2) return

    const newFlippedCards = [...flippedCards]
    newFlippedCards[index] = true
    setFlippedCards(newFlippedCards)
    setSelectedCards((prevSelected) => [...prevSelected, card])
  }

  const resetGame = () => {
    setFlippedCards(Array(12).fill(false))
    setSelectedCards([])
    setIsMatch(false)
    setSuccess(false)
    setConfetti(false)
    setProgress(0)
    setSuccessModalShow(false)
    setWrongModalShow(false)
  }

  const handleModalClose = () => {
    resetGame()
  }

  const handleNext = () => {
    resetGame()
    handleNextExample()
  }

  const handleRetry = () => {
    resetGame()
  }

  return (
    <div>
      {confetti && <Confetti recycle={false} numberOfPieces={800} />}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', // 3 columns
          gridTemplateRows: 'repeat(4, 100px)', // 4 rows
          gap: '10px'
        }}
      >
        {cards.map((card, index) => (
          <div
            key={index}
            onClick={() => handleCardClick(index, card)}
            style={{
              width: '100px',
              height: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: flippedCards[index] ? 'rgb(217 235 255)' : '#83d1749e',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'transform 0.3s',
              transform: flippedCards[index] ? 'rotateY(0deg)' : 'rotateY(180deg)',
              perspective: '1000px',
              transformStyle: 'preserve-3d'
            }}
          >
            <div
              style={{
                backfaceVisibility: 'hidden',
                position: 'absolute',
                transform: flippedCards[index] ? 'rotateY(0)' : 'rotateY(180deg)'
              }}
            >
              {flippedCards[index] ? (
                // Check if the card is a unique element or an image
                uniqueElements.includes(card) ? (
                  <span style={{ fontSize: '2rem' }}>{card}</span>
                ) : (
                  <img
                    src={card} // Render the image if it's not a unique element
                    alt='card'
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '8px',
                      objectFit: 'cover'
                    }}
                  />
                )
              ) : (
                '?'
              )}
            </div>
          </div>
        ))}
      </div>

      {successModalShow && (
        <div className='party-animation'>
          <div className='party-popup'>
            <div className='text-end close-button-container mb-2'>
              <CloseButton onClick={handleModalClose} />
            </div>
            <h2>सही उत्तर! आपने सही विकल्प चुना है।</h2>
            <div className='party-cracker'>🎉</div>
            <button onClick={handleNext} className='btn btn-success mt-4'>
              धन्यवाद
            </button>
            <div className='progress-bar success'>
              <div className='progress-bar-inner' style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}

      {wrongModalShow && (
        <div className='wrong-animation-popup'>
          <div className='wrong-popup'>
            <div className='text-end close-button-container mb-2'>
              <CloseButton onClick={handleModalClose} />
            </div>
            <h2>अरे नहीं! सही उत्तर नहीं था!</h2>
            <div className='sad-face'>😢</div>
            <button onClick={handleRetry} className='btn btn-danger mt-4'>
              फिर से प्रयास करें
            </button>
            <div className='progress-bar wrong'>
              <div className='progress-bar-inner' style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemoryGame
